import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  model,
  output,
  QueryList,
  runInInjectionContext,
  Signal,
  untracked,
  ViewChild,
  ViewChildren
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye, faFileExport, faFileImport, faGlobe, faSquareMinus, faSquarePlus } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { Option, UiAdminModule } from '@eiq/ui';
import { Translation, TranslationEntry } from '@empathy/survey-core';

import { TranslationExportOptions } from '../../../models/translations/translation-export-options.interface';
import { TranslationGroup } from '../../../models/translations/translation-group.model';
import { translationGroupConfig, translationGroupKeys } from '../../../models/translations/translation-groups.constant';
import { optionKeyRegex, questionKeyRegex } from '../../../models/translations/translation-key.constant';
import { TranslationRow } from '../../../models/translations/translation-row.model';
import { TranslationTabs } from '../../../models/translations/translation-tabs.enum';
import { TranslationsImportResult } from '../../../models/translations/translations-import-result.interface';
import { EditorTranslationsService } from '../../../services/editor-translations.service';
import { ExportTranslationsService } from '../../../services/export-translations.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ExportTranslationsDialogComponent } from '../export-translations-dialog/export-translations-dialog.component';
import { ImportTranslationsDialogComponent } from '../import-translations-dialog/import-translations-dialog.component';
import { LanguagesEmptyComponent } from '../languages-empty/languages-empty.component';
import { TranslationEntryComponent } from '../translation-entry/translation-entry.component';
import { TranslationsStatusComponent } from '../translations-status/translations-status.component';

@Component({
  standalone: true,
  selector: 'empathy-translations-editor',
  templateUrl: 'translations-editor.component.html',
  styleUrls: ['translations-editor.component.scss'],
  imports: [FontAwesomeModule, FormsModule, LanguagesEmptyComponent, MatCheckbox, TranslationEntryComponent, TranslationsStatusComponent, UiAdminModule]
})
export class TranslationsEditorComponent {
  @ViewChildren('translationGroupAnchor', { read: ElementRef })
  private translationGroupAnchors!: QueryList<ElementRef<HTMLElement>>;

  @ViewChild('editorHeader', { read: ElementRef })
  private editorHeader?: ElementRef<HTMLElement>;

  addLanguage = output<void>();

  translationsService = inject(EditorTranslationsService);

  languageOptions$: Signal<Option<string>[]> = computed(() =>
    this.translationsService
      .translatedLanguages$()
      .filter((language) => language.code !== this.translationsService.defaultLanguageCode$())
      .map((language) => ({
        value: language.code,
        text: language.name
      }))
  );

  defaultTranslation$ = this.translationsService.defaultTranslation$;
  canAddLanguage$ = computed(() => !!this.translationsService.defaultTranslation$());
  activeTranslation$ = this.translationsService.activeTranslation$;
  activeTranslationLanguage$ = this.translationsService.activeTranslationLanguage$;
  activeTranslationGroupKey$ = this.translationsService.activeTranslationGroupKey$;

  groups$ = computed(() => this.buildGroups(this.defaultTranslation$(), this.activeTranslation$()));

  visibleKeys$ = computed(() => this.computeVisibleKeys());

  filteredGroups$ = computed(() => {
    const groups = this.groups$();
    const visibleKeys = this.visibleKeys$();

    if (!visibleKeys) {
      return groups;
    }

    return groups.map((group) => ({ ...group, rows: group.rows.filter((row) => visibleKeys.has(row.key)) })).filter((group) => group.rows.length > 0);
  });

  selectedLanguageCode$ = model<string | null>(null);
  search$ = model('');
  missing$ = model(false);
  outdated$ = model(false);
  ignored$ = model(false);
  identical$ = model(false);

  faGlobe = faGlobe;
  faChevronRight = faChevronRight;
  faSquareMinus = faSquareMinus;
  faSquarePlus = faSquarePlus;
  faEye = faEye;
  faFileExport = faFileExport;
  faFileImport = faFileImport;

  translationTabs = TranslationTabs;

  private injector = inject(Injector);
  private surveyEditorService = inject(SurveyEditorService);
  private exportTranslationsService = inject(ExportTranslationsService);
  private dialog = inject(MatDialog);

  constructor() {
    effect(
      () => {
        const active = this.activeTranslation$();

        if (!active) {
          return;
        }

        this.selectedLanguageCode$.set(active.code);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const activeTranslationId = this.translationsService.activeTranslationId$();

        if (!activeTranslationId) {
          return;
        }

        this.resetFilters();
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const activeGroupKey = this.translationsService.activeTranslationGroupKey$();
        const scrollRequested = this.translationsService.translationGroupScrollRequested$();

        if (!scrollRequested || !activeGroupKey) {
          return;
        }

        this.scrollTo(activeGroupKey);
        this.translationsService.translationGroupScrollRequested$.set(false);
      },
      { allowSignalWrites: true }
    );
  }

  onExport(): void {
    this.dialog
      .open(ExportTranslationsDialogComponent, { data: { languageName: this.activeTranslationLanguage$().name } })
      .afterClosed()
      .subscribe((options: TranslationExportOptions | undefined) => {
        if (!options) {
          return;
        }

        const surveyTitle = this.surveyEditorService.surveyEditor$().model.title;
        this.exportTranslationsService.export(surveyTitle, this.defaultTranslation$(), this.activeTranslation$(), options);
      });
  }

  onImport(): void {
    this.dialog
      .open(ImportTranslationsDialogComponent, { data: { translation: this.activeTranslation$() }, width: '600px', maxHeight: '70vh' })
      .afterClosed()
      .subscribe((result: TranslationsImportResult) => {
        if (result) {
          const translationId = this.activeTranslation$()?.id;

          if (!translationId) {
            return;
          }

          this.translationsService.updateTranslationEntries(translationId, result.entries);
        }
      });
  }

  onEntryChange(key: string, value: string): void {
    const translationId = this.activeTranslation$()?.id;

    if (!translationId) {
      return;
    }

    this.translationsService.updateTranslationEntryValue(translationId, key, value);
  }

  onEntryIgnored(key: string, ignored: boolean): void {
    const translationId = this.activeTranslation$()?.id;

    if (!translationId) {
      return;
    }

    this.translationsService.setTranslationEntryIgnored(translationId, key, ignored);
  }

  onQuestionIgnored(questionId: string, ignored: boolean): void {
    const translationId = this.activeTranslation$()?.id;

    if (!translationId) {
      return;
    }

    this.translationsService.setQuestionIgnored(translationId, questionId, ignored);
  }

  private indexEntries(t: Translation | null): Map<string, TranslationEntry> {
    return new Map((t?.entries ?? []).map((e) => [e.key, e]));
  }

  private buildGroups(defaultTranslation: Translation | null, activeTranslation: Translation | null): TranslationGroup[] {
    if (!defaultTranslation || !activeTranslation) {
      return [];
    }

    const defIndex = this.indexEntries(defaultTranslation);
    const actIndex = this.indexEntries(activeTranslation);

    const keys = (defaultTranslation?.entries ?? []).map((entry) => entry.key);

    const groupMap = new Map<string, TranslationRow[]>();

    for (const key of keys) {
      const isQuestionTranslation = questionKeyRegex.test(key);
      const isOptionTranslation = optionKeyRegex.test(key);

      let displayKey = key;

      if (isQuestionTranslation) {
        const questionId = key.split('.')[0];
        const questionCode = this.questionCode(questionId);
        displayKey = displayKey.replace(questionId, questionCode);
      }

      const row: TranslationRow = {
        key,
        displayKey,
        defaultEntry: defIndex.get(key),
        translatedEntry: actIndex.get(key),
        languageName: this.activeTranslationLanguage$()?.name ?? '',
        isQuestionTranslation,
        isOptionTranslation
      };

      const groupKey = key.split('.')[0];
      const group = groupMap.get(groupKey) ?? [];
      group.push(row);
      groupMap.set(groupKey, group);
    }

    return Array.from(groupMap.entries())
      .map(([groupKey, rows]) => {
        const config = translationGroupConfig.find((c) => c.key === groupKey);

        const isQuestionGroup = rows.some((r) => r.isQuestionTranslation);

        const groupName = isQuestionGroup ? this.questionCode(groupKey) : config?.name;

        return new TranslationGroup(groupKey, groupName, rows);
      })
      .sort((a, b) => {
        const rank = (k: string): number => {
          if (k === translationGroupKeys.welcomeScreen) {
            return 0;
          }
          if (k === translationGroupKeys.thankYouPage) {
            return 2;
          }
          return 1;
        };

        const ra = rank(a.groupKey);
        const rb = rank(b.groupKey);

        if (ra !== rb) {
          return ra - rb;
        }

        return 0;
      });
  }

  private questionCode(questionId: string): string | undefined {
    const question = this.surveyEditorService.surveyEditor$().questions.find((q) => q.id === questionId);

    if (question) {
      return question.code;
    }
  }

  private resetFilters(): void {
    this.search$.set('');
    this.missing$.set(false);
    this.outdated$.set(false);
    this.ignored$.set(false);
    this.identical$.set(false);
  }

  private computeVisibleKeys(): Set<string> | null {
    const query = this.search$().trim().toLowerCase();

    const showMissing = this.missing$();
    const showOutdated = this.outdated$();
    const showIdentical = this.identical$();
    const showIgnored = this.ignored$();

    const isAnyStatusFilterEnabled = showMissing || showOutdated || showIdentical || showIgnored;

    if (!isAnyStatusFilterEnabled && !query) {
      return null;
    }

    const groups = untracked(this.groups$);

    const translationEntryCounts = this.buildDuplicateCounts(groups, showIdentical);

    const visibleKeys = new Set<string>();

    for (const group of groups) {
      for (const row of group.rows) {
        const defaultValue = (row.defaultEntry?.value ?? '').trim();
        const activeValue = (row.translatedEntry?.value ?? '').trim();

        const isIgnored = !!row.translatedEntry?.ignoredInd;
        const isOutdated = !!row.translatedEntry?.outdatedInd;
        const isMissing = defaultValue.length > 0 && activeValue.length === 0 && !isIgnored;
        const isIdentical = this.isIdentical(row, translationEntryCounts);

        const queryMatches = this.matchesQuery(row, query);

        if (!isAnyStatusFilterEnabled) {
          if (queryMatches) {
            visibleKeys.add(row.key);
          }
          continue;
        }

        const statusMatches = (showMissing && isMissing) || (showOutdated && isOutdated) || (showIdentical && isIdentical) || (showIgnored && isIgnored);

        if (statusMatches && queryMatches) {
          visibleKeys.add(row.key);
        }
      }
    }

    return visibleKeys;
  }

  private buildDuplicateCounts(groups: TranslationGroup[], enabled: boolean): Map<string, number> | null {
    if (!enabled) {
      return null;
    }

    return groups
      .flatMap((g) => g.rows)
      .reduce((map, row) => {
        const value = (row.translatedEntry?.value ?? '').trim();
        if (!value) {
          return map;
        }

        map.set(value, (map.get(value) ?? 0) + 1);
        return map;
      }, new Map<string, number>());
  }

  private isIdentical(row: TranslationRow, duplicateCounts: Map<string, number> | null): boolean {
    if (!duplicateCounts) {
      return false;
    }

    const value = (row.translatedEntry?.value ?? '').trim();
    if (!value) {
      return false;
    }

    return (duplicateCounts.get(value) ?? 0) > 1;
  }

  private matchesQuery(row: TranslationRow, query: string): boolean {
    if (!query) {
      return true;
    }

    const key = (row.key ?? '').toLowerCase();
    const defaultValue = (row.defaultEntry?.value ?? '').trim().toLowerCase();
    const activeValue = (row.translatedEntry?.value ?? '').trim().toLowerCase();

    return key.includes(query) || defaultValue.includes(query) || activeValue.includes(query);
  }

  private scrollTo(groupKey: string): void {
    runInInjectionContext(this.injector, () => {
      afterNextRender(() => {
        const target = this.translationGroupAnchors
          .toArray()
          .map((x) => x.nativeElement)
          .find((el) => el.getAttribute('data-translation-group-key') === groupKey);

        if (!target) {
          return;
        }

        target.style.scrollMarginTop = `${this.editorHeader?.nativeElement.offsetHeight ?? 0}px`;
        target.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
      });
    });
  }
}
